import { Authorizer, OlympusAuthorizationProvider } from '@owner/auth'
import type { OlympusUser, OlympusBrandSession } from '@owner/auth'

import { logError } from '@/logger/logger'

import type { BrandSession, User } from './types'

let authorizerInstance: Authorizer | null = null

/**
 * Get the Authorizer singleton instance
 * Components use this to access authorizer.can(), authorizer.hasRole(), etc. directly
 */
export const useAuthorizer = (): Authorizer => {
  if (!authorizerInstance) {
    throw new Error(
      'Authorizer not initialized. Call initializeAuthorizer() after login.'
    )
  }
  return authorizerInstance
}

const initializeAuthorizer = async (
  user: OlympusUser,
  brandSession: OlympusBrandSession
): Promise<void> => {
  const provider = new OlympusAuthorizationProvider(user, brandSession)
  authorizerInstance = new Authorizer({
    // No background refresh needed - Hermes re-initializes on login/brand switch
    autoRefresh: false,
    provider,
  })
  await authorizerInstance.initialize()
}

export const resetAuthorizer = (): void => {
  if (authorizerInstance) {
    authorizerInstance.destroy()
  }
  authorizerInstance = null
}

/**
 * Get the Authorizer instance if initialized, or null otherwise
 * Use this for conditional checks where you need TypeScript type narrowing
 */
export const getAuthorizer = (): Authorizer | null => {
  return authorizerInstance
}

/**
 * Safely initialize the Authorizer with feature flag and validation checks.
 * This function is idempotent - safe to call multiple times.
 * Errors are logged but don't block the calling flow.
 * @param user - The authenticated user
 * @param brandSession - The brand session to initialize the authorizer for
 * @param context - Context string for error logging
 */
export const safeInitializeAuthorizer = async (
  user: User,
  brandSession: BrandSession,
  context: string
): Promise<void> => {
  if (getAuthorizer()) {
    return
  }

  try {
    await initializeAuthorizer(user, brandSession)
  } catch (error) {
    logError(error, { context: `${context} - Failed to initialize Authorizer` })
  }
}
